// Shared formatting for the Collection Report tab — mirrors the sheet's number formats and red-under-target rule.
// Money follows the collections desk region (GBP for UK, AUD for AU) via the regionFmt module flag.
import { getCollectionsRegion } from "@/lib/regionFmt";

const money = (v, dp) => {
  if (v === null || v === undefined) return "—";
  const aus = getCollectionsRegion() === "AUS";
  return new Intl.NumberFormat(aus ? "en-AU" : "en-GB", { style: "currency", currency: aus ? "AUD" : "GBP", minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);
};
export const gbp0 = (v) => money(v, 0);
export const gbp2 = (v) => money(v, 2);
export const money0 = gbp0;
export const money2 = gbp2;
export const num2 = (v) => (v === null || v === undefined ? "—" : new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v));
export const pct = (v, d = 2) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(d)}%`);
export const int = (v) => (v === null || v === undefined ? "—" : new Intl.NumberFormat("en-GB").format(v));

export const pctTone = (v) => (v === null || v === undefined ? "text-slate-400" : v < 100 ? "text-red-600" : "text-emerald-700");
export const varTone = (v) => (v === null || v === undefined ? "text-slate-400" : v < 0 ? "text-red-600" : "text-emerald-700");

export const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
};

export const timeOnly = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—");
export const dateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export const EDITABLE_COLS = [
  { key: "loan_due", label: "Loan Due", fmt: gbp2, kind: "money" },
  { key: "cash_collected", label: "Cash Collected", fmt: gbp2, kind: "money" },
  { key: "cash_dd", label: "Direct Debit (BECS)", fmt: gbp2, kind: "money" },
  { key: "cash_payto", label: "PayTo", fmt: gbp2, kind: "money" },
  { key: "cash_online", label: "Online / other", fmt: gbp2, kind: "money" },
  { key: "gc_failed_value", label: "Failed DD value", fmt: gbp2, kind: "money" },
  { key: "total_receivables", label: "Total Receivables", fmt: num2, kind: "money" },
  { key: "total_arrears", label: "Total Arrears", fmt: num2, kind: "money" },
  { key: "principal_in_arrears", label: "Principal O/S (in arrears)", fmt: num2, kind: "money" },
  { key: "total_paid_in_arrears", label: "Total Paid (in arrears)", fmt: num2, kind: "money" },
  { key: "arrears_count", label: "Number of Arrears", fmt: int, kind: "int" },
  { key: "arrears_1_7_days", label: "Day 1-7", fmt: int, kind: "int" },
  { key: "arrears_8_30_days", label: "Day 8-30", fmt: int, kind: "int" },
  { key: "arrears_30_plus_days", label: "Day 30+", fmt: int, kind: "int" },
  { key: "loan_book_maturity_pct", label: "Loan Book Maturity %", fmt: (v) => pct(v), kind: "pct" },
  { key: "total_open_loans", label: "Total Open Loans", fmt: int, kind: "int" },
];

// Colours for the DD / PayTo / online split — same on the grid and inside the analysis bars.
export const SPLIT = [
  { key: "cash_dd", label: "Direct Debit", color: "#0f766e" },
  { key: "cash_payto", label: "PayTo", color: "#7c3aed" },
  { key: "cash_online", label: "Online / other", color: "#f59e0b" },
];

// Human-readable message from an axios error — FastAPI 422s carry an array of {loc,msg}, never render that as a React child.
export const apiError = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (typeof x === "string" ? x : x?.msg || JSON.stringify(x))).join("; ");
  return e?.message || fallback;
};
